<template>
  <PriorHeader />
  <v-container>
    <v-row>
      <v-col cols="12">
        <h1 class="text-primary weight-600 pb-4">{{ pageTitle }}</h1>
        <div v-if="preloader">
          <SubmissionPageSkeleton />
        </div>
        <div v-else>
          <v-row class="submission-status-wrapper">
            <v-col cols="12" class="lh--available-orders-container">
              <SubmissionDetailCard :order="orderInfo" :buttons-visible="false" :fax-forms="faxDestinationOptions" />
            </v-col>
          </v-row>
          <v-row class="submission-status-wrapper">
            <v-col cols="12" class="lh--available-orders-container">
              <PolicyReviewCard :order="orderInfo" />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="submission-detail-section">
              <AllFormStepsView ref="allFormStepsRef" />
            </v-col>
          </v-row>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col v-if="orderInfo" cols="12">
        <OutgoingFaxEditor
          :types="[]"
          :order-info="orderInfo"
          :to-fax-number="orderInfo.providerContactFax"
          :attachment-options="attachmentOptions"
          title="Send Fax"
          @on-complete="onFaxComplete"
          @on-pre-send="saveOrderDraft" />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <AuditLogList ref="auditLogListRef" :order-info-id="route.params.id" />
      </v-col>
    </v-row>

    <v-snackbar v-model="showSuccessNotification" color="success" timeout="5000" location="top">
      Fax sent successfully!
    </v-snackbar>
  </v-container>
  <PriorFooter />
</template>

<script setup>
import PriorFooter from "@/components/PriorFooter.vue";
import PriorHeader from "@/components/PriorHeader.vue";
import AllFormStepsView from "@/pages/AllFormStepsView";
import SubmissionDetailCard from "@/components/SubmissionDetailCard.vue";
import SubmissionPageSkeleton from "@/components/SubmissionPageSkeleton.vue";
import AuditLogList from "@/components/AuditLogList.vue";
import OutgoingFaxEditor from "@/components/OutgoingFaxEditor.vue";

import { onBeforeMount, ref, computed } from "vue";
import { onBeforeRouteLeave, useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { clearOrderInfoStorage, convertDateFormat } from "@/utils";

import { orderInfoService } from "@/services/orderInfoService";
import { mainServices } from "@/services/mainServices";
import { useOrderInfoStore } from "@/stores/orderInfoStore";

import PolicyReviewCard from "@/components/PolicyReviewCard.vue";

const { orderInfo } = storeToRefs(useOrderInfoStore());
const route = useRoute();

const preloader = ref(false);
const pageTitle = ref("Review Submission");
const faxDestinationOptions = ref([]);
const faxAttachments = ref([]);
const showSuccessNotification = ref(false);
const auditLogListRef = ref(null);
const allFormStepsRef = ref(null);

const attachmentOptions = computed(() => {
  const options = [];

  // Response faxes from faxAttachments
  faxAttachments.value.forEach((a, index) => {
    options.push({
      title: `Response Fax ${index + 1}: ${a.label} ${
        a.timestamp ? "Received on " + convertDateFormat(a.timestamp) : ""
      }`,
      value: {
        type: "response_fax",
        s3_url: a.link,
      },
    });
  });

  // Flattened form (for OrderInfo only)
  if (
    orderInfo.value?.editableSubmissionForm &&
    orderInfo.value?.submissionMethod === "eFax" &&
    orderInfo.value?.orderKey
  ) {
    options.push({
      title: orderInfo.value?.faxDescription ? "Filled " + orderInfo.value?.faxDescription : "Filled Prior Auth Form",
      value: {
        type: "flattened_form",
        s3_url: null,
      },
    });
  }

  return options;
});

const incompleteStatuses = ["Created", "Incomplete", "Review demographics info"];

onBeforeMount(() => {
  if (route.params.id) {
    clearOrderInfoStorage();
    loadOrder(route.params.id);
  }
  getFaxForms();
  startStaffTime(route.params.id);
  window.addEventListener("beforeunload", onBeforeUnload);
});

async function getFaxForms() {
  faxDestinationOptions.value = await mainServices.getFaxForms();
}

async function loadOrder(orderKey) {
  preloader.value = true;
  const fetchedOrder = await orderInfoService.getOrderDetails(orderKey);
  orderInfo.value = fetchedOrder.orderInfo;
  faxAttachments.value = fetchedOrder.orderInfo.orderSubmission.responseDocuments || [];
  pageTitle.value = incompleteStatuses.includes(orderInfo.value.status)
    ? "Review Submission and Send to Processing"
    : "Review Submission and Send to Plan";
  preloader.value = false;
}

const onBeforeUnload = () => {
  updateStaffTime();
};

onBeforeRouteLeave(async () => {
  await updateStaffTime();
});

const startStaffTime = (orderKey) => {
  try {
    orderInfoService.startOrderStaffTime(orderKey);
  } catch (e) {
    // Suppress
  }
};

const updateStaffTime = async () => {
  const orderKey = route.params.id;
  try {
    await orderInfoService.pushOrderStaffTime(orderKey);
  } catch (e) {
    // Suppress
  }
};

const saveOrderDraft = async (resolve, reject) => {
  try {
    if (allFormStepsRef.value?.saveDraft) {
      await allFormStepsRef.value.saveDraft();
    }
    resolve();
  } catch (error) {
    reject(error);
  }
};

const onFaxComplete = ({ sent }) => {
  if (sent) {
    showSuccessNotification.value = true;
    // Refresh audit log to show the new fax entry
    if (auditLogListRef.value) {
      auditLogListRef.value.refresh();
    }
  }
};
</script>

<style lang="scss" scoped></style>
